import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { FreeSpinOverlayEvent, SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { ArcadeWheelEvent } from "@lib/titan2-slingo/dist/game/view/abstract/display/ArcadeWheel";
import { SpinsState } from "@lib/titan2-slingo/dist/game/view/abstract/display/SpinsRemaining";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { ResizeEvent } from "@lib/titan2/dist/game/renderer/Renderer";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { GameEvent } from "../../../controller/event/GameEvent";
import { GameSpineUtil } from "../../../utils/GameSpineUtil";
import { GameLang } from "../lang/GameLang";
/**
 * GameSpinsRemaining.ts
 */
export class GameSpinsRemaining extends AbstractGameView {
    create() {
        super.create();
        Logger.addObjectRef(this, "SpinsRemaining");
        this.state = SpinsState.START;
        this.lastState = this.state;
        const spinsLabelText = TitanText.createField(this, this.landscapeProperties.spinsLabelProperties, this.portraitProperties?.spinsLabelProperties);
        this.spinsValueText = TitanText.createField(this, this.landscapeProperties.spinsValueProperties, this.portraitProperties?.spinsValueProperties);
        const purchaseSpinsLabelText = TitanText.createField(this, this.landscapeProperties.purchaseSpinsLabelProperties, this.portraitProperties?.purchaseSpinsLabelProperties);
        this.purchaseSpinsValueText = TitanText.createField(this, this.landscapeProperties.purchaseSpinsValueProperties, this.portraitProperties?.purchaseSpinsValueProperties);
        // Add the values to the spine's dynamic value slots.
        this.counterSpine = TitanSpine.createSpine(this, this.landscapeProperties.counterSpineProperties, this.portraitProperties?.counterSpineProperties);
        //In an attempt to save performance, on a rotation, titan text will destroy its text object and then recreate it (this causes worse performance)
        //however this resets our texture, which we are using for the slot. Therefore we must remove this "feature" of Titan Text.
        spinsLabelText.unregisterCore(ResizeEvent.DEINITIALISE_TEXT);
        purchaseSpinsLabelText.unregisterCore(ResizeEvent.DEINITIALISE_TEXT);
        // Write the label textures to the spine's dynamic label slots, hiding the original text objects.
        GameSpineUtil.bindTextToSlot(this.counterSpine, "dynamic-label-standard", spinsLabelText);
        GameSpineUtil.bindTextToSlot(this.counterSpine, "dynamic-label-purchase", purchaseSpinsLabelText);
        // Control the values through counters.
        const spinsCounter = new SpinsCounter(this.spinsValueText, [spinsLabelText]);
        const purchaseSpinsCounter = new PurchaseSpinsCounter(this.purchaseSpinsValueText, [purchaseSpinsLabelText]);
        // Map the spins counters to their respective states. Free spins feeds into the standard spins counter.
        this.spinsCounters = new Map();
        this.spinsCounters.set(SpinsState.STANDARD, spinsCounter);
        this.spinsCounters.set(SpinsState.PURCHASE, purchaseSpinsCounter);
        this.spinsCounters.set(SpinsState.FREE, spinsCounter);
        this.locationManager.add("FreeSpinCounter", this.spinsValueText);
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(CommonGameEvent.SPIN, this.handleStandardSpin);
        this.registerGame(CommonGameEvent.FREE_SPIN, this.handleFreeSpin);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN, this.handlePurchaseSpin);
        this.registerGame(SlingoEvent.GAME_SUMMARY, this.handleGameSummary);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.handlePurchaseSpinRecovery);
        this.registerGame(SlingoEvent.SPIN_RESULT, this.handleSpinResult);
        this.registerGame(SlingoEvent.DAB_COMPLETE, this.handleDabComplete);
        this.registerGame(CommonGameEvent.REPLAY_PURCHASE_ENTRY, this.handleReplayPurchaseEntry);
        this.registerGame(FreeSpinOverlayEvent.FREE_SPIN_END, this.handleFreeSpinAwardEnd);
        this.registerGame(ArcadeWheelEvent.SHOW, this.handleArcadeWheelShow);
        this.registerGame(ArcadeWheelEvent.HIDE, this.handleArcadeWheelHide);
        this.registerGame(ArcadeWheelEvent.WIN, this.handleArcadeWheelHide);
        this.registerGame(ArcadeWheelEvent.REPLAY_SPIN, this.handleArcadeWheelReplaySpin);
        this.registerGame(ArcadeWheelEvent.REPLAY_SPIN_COMPLETE, this.handleArcadeWheelReplaySpinComplete);
        this.registerGame(GameEvent.NEXT_GAME_TRANSITION, this.handleNextGameTransition);
    }
    handleSpinResult() {
        this.spinsCounters.get(this.state)?.decrementSpins();
        SpineUtil.play(this.counterSpine, "value-decrease", null, null, 1);
    }
    handleReplayPurchaseEntry() {
        this.updateSpinsRemaining();
        this.handleStandardSpin();
    }
    handleNextGameTransition() {
        this.updateState(SpinsState.START);
        this.resetSpinsRemaining();
    }
    handlePurchaseEntry() {
        this.updateState(SpinsState.START);
        this.resetSpinsRemaining();
        this.handleStandardSpin();
    }
    handleFreeSpinAwardEnd() {
        this.spinsCounters.get(SpinsState.FREE)?.incrementSpins();
        SpineUtil.play(this.counterSpine, "value-increase", null, null, 1);
    }
    handleStandardSpin() {
        if (Service.isReplay() && Service.isReplaySpinsComplete()) {
            return; // replayed stakeToSpin purchase spins come in looking like standard spins :S
        }
        this.updateState(SpinsState.STANDARD);
    }
    handlePurchaseSpin() {
        this.updateState(SpinsState.PURCHASE);
    }
    handleFreeSpin() {
        this.updateState(SpinsState.FREE);
    }
    handleGameSummary() {
        this.handleArcadeWheelHide();
    }
    handleStandardSpinRecovery() {
        this.updateState(SpinsState.STANDARD);
        this.updateSpinsRemaining();
    }
    handleFreeSpinRecovery() {
        this.updateState(SpinsState.FREE);
        this.updateSpinsRemaining();
    }
    handlePurchaseSpinRecovery() {
        this.updateState(SpinsState.PURCHASE);
        this.updateSpinsRemaining();
    }
    handleDabComplete() {
        this.updateSpinsRemaining();
    }
    handleArcadeWheelReplaySpinComplete() {
        if (Service.isReplay()) {
            this.updateSpinsRemaining();
        }
    }
    handleArcadeWheelReplaySpin() {
        this.updateState(SpinsState.PURCHASE);
        this.handleArcadeWheelShow();
    }
    handleArcadeWheelShow() {
        this.spinsCounters.get(SpinsState.PURCHASE).valueText.alpha = 0.5;
    }
    handleArcadeWheelHide() {
        this.spinsCounters.get(SpinsState.PURCHASE).valueText.alpha = 1;
    }
    handleRotate(event) {
        super.handleRotate(event);
        GameSpineUtil.addToSlot(this.counterSpine, this.spinsValueText, "dynamic-value-standard");
        GameSpineUtil.addToSlot(this.counterSpine, this.purchaseSpinsValueText, "dynamic-value-purchase");
    }
    resetSpinsRemaining() {
        const config = this.model.getGameConfig();
        this.spinsCounters.get(SpinsState.STANDARD)?.setSpins(config.getNumberOfSpins());
        this.spinsCounters.get(SpinsState.PURCHASE)?.setSpins(this.responsibleGaming.getPurchaseSpinsRemaining());
    }
    updateSpinsRemaining() {
        const progress = this.model.getGameProgress();
        const dabData = this.model.getDabData();
        const freeSpinsDeferred = dabData.hasJokerCellToDab() || dabData.hasSuperJokerCellToDab() ? this.model.getSpinResult().getReelSequence().getFreeSpinIndexes().length : 0;
        const spinsRemaining = progress.getSpinsRemaining() + progress.getFreeSpinsRemaining() - freeSpinsDeferred;
        this.spinsCounters.get(SpinsState.STANDARD)?.setSpins(spinsRemaining);
        this.spinsCounters.get(SpinsState.PURCHASE)?.setSpins(this.responsibleGaming.getPurchaseSpinsRemaining());
    }
    updateState(state) {
        if (state !== this.state) {
            this.lastState = this.state;
            this.state = state;
            if (this.state === SpinsState.PURCHASE || this.lastState === SpinsState.PURCHASE) {
                SpineUtil.play(this.counterSpine, "standard-to-purchase", null, null, 0);
                GameSpineUtil.setReversed(this.counterSpine, 0, this.state !== SpinsState.PURCHASE);
            }
        }
    }
}
class SpinsCounter {
    constructor(valueText, labels) {
        this.valueText = valueText;
        this.labels = labels;
        this.spins = NaN;
    }
    getSpins() {
        return this.spins;
    }
    setSpins(spins) {
        if (this.spins !== spins) {
            this.spins = spins;
            if (this.spins === 1) {
                this.labels.forEach(x => x.setLangKey(GameLang.SPIN));
            }
            else {
                this.labels.forEach(x => x.setLangKey(GameLang.SPINS));
            }
            this.updateText();
        }
    }
    incrementSpins() {
        this.setSpins(this.spins + 1);
    }
    decrementSpins() {
        this.setSpins(this.spins - 1);
    }
    getSpinsMessage() {
        return this.getSpins().toString();
    }
    updateText() {
        this.valueText.setText(this.getSpinsMessage());
    }
}
class PurchaseSpinsCounter extends SpinsCounter {
    getSpinsMessage() {
        // This mirrors the base SpinsRemaining implementation which shows "∞" when over 100.
        if (this.getSpins() > 100) {
            return "∞";
        }
        return super.getSpinsMessage();
    }
}
//# sourceMappingURL=GameSpinsRemaining.js.map